import { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { useConfirm } from '../hooks/useConfirm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Card, CardContent } from '@/components/ui/card';
import { Pause, Play, TrendingUp, TrendingDown, Activity, RefreshCw, AlertCircle, CheckCircle2, BarChart3, FlaskConical, Trash2, ClockArrowDown, Wallet, MoreVertical, Calculator, Pencil, ClockArrowUp, ShieldAlert, OctagonX, LineChart } from 'lucide-react';
import EquityChart from '../components/EquityChart';
import SignalChartDialog from '../components/signal-chart/SignalChartDialog';
import TradeVisualizer from '../components/TradeVisualizer'
import { useOnWindowResize } from '../lib/useOnWindowResize';

const TAB_POSITIONS  = 'positions';
const TAB_ORDERS     = 'orders';
const TAB_TRADES     = 'trades';
const TAB_EQUITY     = 'ledger';
const TAB_DIVERGENCE = 'divergence';
const TAB_RECONCILE  = 'reconcile';

const TAB_LABELS = { [TAB_DIVERGENCE]: 'Strategy Sync' };

const POLL_INTERVAL_MS = 60000;

// Live price stream comes from market-sync's WebSocket server, not the Flask API. In dev it's a
// separate port; in production nginx serves it on the site's own origin under /ws/.
const WS_BASE = process.env.REACT_APP_WS_URL
  ?? (process.env.NODE_ENV === 'development'
    ? 'ws://localhost:5001'
    : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`);

const POSITIONS_HEAD = ['Symbol', 'Shares', 'Entry Date', 'Entry ₹', 'Current ₹', 'Value', 'Unrealised P&L', 'Days', ''];

// Formatters
const fmtPct = (v) => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—';
const fmtMoney = (v) => v != null ? `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';
const fmtDate = (s) => s ? s.slice(0, 10) : '—';
const fmtPrice = (v) => v != null ? `₹${v.toFixed(2)}` : '—';

const describeHaltReason = (book) => {
  switch (book.entries_halted_reason) {
    case 'daily_loss_limit':   return `daily loss limit (${book.daily_loss_limit_pct}%) was breached`;
    case 'intraday_loss_limit': return `intraday loss limit (${book.daily_loss_limit_pct}%) was breached`;
    case 'manual_flatten':     return 'the book was manually flattened';
    default:                   return book.entries_halted_reason || 'kill switch tripped';
  }
};

function DataTable({ head, rows, empty }) {
  if (!rows?.length) return <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">{empty}</div>;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card hidden md:block">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border bg-surface-hover/50">
            {head.map((h, i) => <th key={i} className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

function ReconcileMobileCard({ symbol, fields, action }) {
  return (
    <div className="p-4 bg-card shadow-sm bg-surface-base border border-border rounded-xl flex flex-col gap-3">
      <span className="font-bold text-base text-foreground">{symbol.replace('.NS', '')}</span>
      <div className="grid grid-cols-2 gap-y-2 text-xs">
        {fields.map(([label, value]) => (
          <div key={label} className="flex flex-col">
            <span className="text-[10px] text-muted-foreground uppercase">{label}</span>
            <span className="font-mono">{value}</span>
          </div>
        ))}
      </div>
      {action && <div className="flex gap-2 pt-2 border-t border-border/50">{action}</div>}
    </div>
  );
}

export default function LiveDetail() {
  const { id } = useParams();
  const { confirm, confirmDialog } = useConfirm();
  const [book, setBook]           = useState(null);
  const [positions, setPositions] = useState([]);
  // Position whose indicator chart is open, and whether to pull dependency series.
  const [chartPosition, setChartPosition] = useState(null);
  const [chartDeps, setChartDeps] = useState(false);
  const [orders, setOrders]       = useState([]);
  const [trades, setTrades]       = useState([]);
  const [equity, setEquity]       = useState([]);
  const [divergence, setDivergence] = useState([]);

  const [reconcile, setReconcile]       = useState(null);
  const [reconciling, setReconciling]   = useState(false);

  const [tab, setTab]             = useState(TAB_POSITIONS);
  const [tabLoading, setTabLoading] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [runningDay, setRunningDay] = useState(false);
  const [syncingAuth, setSyncingAuth] = useState(false);
  const [resumingEntries, setResumingEntries] = useState(false);
  const [flattening, setFlattening] = useState(false);
  const [goLiveDialog, setGoLiveDialog] = useState(false);
  const [exitDialog, setExitDialog]     = useState(null);   // { symbol, dbPrice, dbShares } | null
  const [exitPrice, setExitPrice]       = useState('');
  const [exitSubmitting, setExitSubmitting] = useState(false);

  const [fundsDialog, setFundsDialog]       = useState(false);
  const [fundsAmount, setFundsAmount]       = useState('');
  const [fundsSubmitting, setFundsSubmitting] = useState(false);

  const [renameDialog, setRenameDialog]       = useState(false);
  const [renameValue, setRenameValue]         = useState('');
  const [renameSubmitting, setRenameSubmitting] = useState(false);

  const [riskDialog, setRiskDialog]           = useState(false);
  const [riskLimitValue, setRiskLimitValue]   = useState('');
  const [riskSubmitting, setRiskSubmitting]   = useState(false);

  const [liveQuotes, setLiveQuotes]       = useState({});   // symbol → { ltp, direction }
  const [streamStatus, setStreamStatus]   = useState('off'); // 'off'|'connecting'|'live'|'reconnecting'
  const [flashedSymbols, setFlashedSymbols] = useState(new Set());

  const [portfolioLive, setPortfolioLive]     = useState();
  const [pnlLive, setPNLLive]     = useState();

  const pollTimer   = useRef(null);
  const flashTimers = useRef({});
  const reconnectTimer    = useRef(null);
  const reconnectAttempts = useRef(0);
  const wsRef             = useRef(null);

  // The tabs row scrolls horizontally on narrow screens with its scrollbar
  // hidden (`no-scrollbar`), which on its own gives no hint that Strategy Sync
  // / Reconcile are off-screen — track scroll position so we can fade the
  // edges in/out as a "there's more this way" cue.
  const tabScrollRef = useRef(null);
  const [tabOverflow, setTabOverflow] = useState({ left: false, right: false });

  const updateTabOverflow = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    setTabOverflow({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useOnWindowResize(updateTabOverflow);

  const fetchBook = useCallback(() => api.get(`/live/books/${id}`).then(r => setBook(r.data)), [id]);
  const fetchEquity = useCallback(() => api.get(`/live/books/${id}/equity`).then(r => setEquity(r.data)), [id]);

  const fetchTab = useCallback((t) => {
    if (t === TAB_POSITIONS) return api.get(`/live/books/${id}/positions`).then(r => setPositions(r.data));
    if (t === TAB_ORDERS)    return api.get(`/live/books/${id}/orders`).then(r => setOrders(r.data));
    if (t === TAB_TRADES)    return api.get(`/live/books/${id}/trades`).then(r => setTrades(r.data));
    if (t === TAB_EQUITY)    return api.get(`/live/books/${id}/equity`).then(r => setEquity(r.data));
    if (t === TAB_DIVERGENCE) return api.get(`/live/books/${id}/divergence`).then(r => setDivergence(r.data));
    // TAB_RECONCILE is fetched manually
  }, [id]);

  const loadData = useCallback(() => {
    return Promise.all([fetchBook(), fetchTab(tab), fetchEquity()]);
  }, [fetchBook, fetchTab, fetchEquity, tab]);

  useEffect(() => {
    loadData().finally(() => setLoading(false));
    pollTimer.current = setInterval(loadData, POLL_INTERVAL_MS);
    return () => clearInterval(pollTimer.current);
  }, [loadData]);

  // Price stream (market-sync WebSocket) — only when on positions tab, live mode, valid auth
  const canStream = !!(book && book.is_auth_valid);
  useEffect(() => {
    if (tab !== TAB_POSITIONS || !canStream) {
      setStreamStatus('off');
      return;
    }
    const MAX_BACKOFF_MS = 30000;
    // Close codes the server uses to say "don't retry": unauthorized / not your book.
    const NO_RETRY_CODES = [4401, 4404];
    let closed = false; // set on cleanup — guards against reconnect after unmount/dep change

    const connect = () => {
      if (closed) return;
      const token = localStorage.getItem('token');
      const url   = `${WS_BASE}/ws/live/books/${id}/stream?token=${encodeURIComponent(token)}`;

      setStreamStatus(reconnectAttempts.current === 0 ? 'connecting' : 'reconnecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'ready') {
            // Only a fully set-up stream counts as healthy — an open socket the server closes
            // straight away (market data down) must keep backing off, not retry every second.
            reconnectAttempts.current = 0;
            setStreamStatus('live');
            return;
          }
          if (msg.type !== 'tick') return;
          setLiveQuotes(prev => {
            const old = prev[msg.symbol]?.ltp;
            const dir = old == null ? null : msg.ltp > old ? 'up' : msg.ltp < old ? 'down' : null;
            return { ...prev, [msg.symbol]: { ltp: msg.ltp, direction: dir } };
          });
          setFlashedSymbols(prev => new Set([...prev, msg.symbol]));

          if (flashTimers.current[msg.symbol]) clearTimeout(flashTimers.current[msg.symbol]);
          flashTimers.current[msg.symbol] = setTimeout(() => {
            setFlashedSymbols(prev => { const n = new Set(prev); n.delete(msg.symbol); return n; });
            delete flashTimers.current[msg.symbol];
          }, 800);
        } catch (_) { /* ignore parse errors */ }
      };

      // Fires for failed connects as well as dropped ones; we drive reconnection ourselves.
      ws.onclose = (evt) => {
        if (wsRef.current === ws) wsRef.current = null;
        if (closed) return;
        if (NO_RETRY_CODES.includes(evt.code)) {
          setStreamStatus('off');
          return;
        }
        const delay = Math.min(1000 * 2 ** reconnectAttempts.current, MAX_BACKOFF_MS);
        reconnectAttempts.current += 1;
        setStreamStatus('reconnecting');
        reconnectTimer.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer.current);
      reconnectAttempts.current = 0;
      wsRef.current?.close();
      wsRef.current = null;
      setStreamStatus('off');
    };
  }, [id, tab, canStream]); // eslint-disable-line react-hooks/exhaustive-deps

  // Positions open/close while the page is up; tell the server so it streams the current set.
  const openSymbolsKey = (positions || []).filter(p => !p.is_pending).map(p => p.symbol).sort().join(',');
  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'refresh' }));
    }
  }, [openSymbolsKey]);

  useEffect(() => {
    if (book == null || !positions || positions.length === 0) {
      return;
    }
    // Only trust the recomputed value during an active trading session.
    // Neither check alone is enough:
    //  - "today's EOD hasn't run yet" alone breaks on weekends/holidays,
    //    where last_eod_run_date is stuck on the last trading day (never
    //    equal to today) — the override would stay on indefinitely, diffing
    //    stale last-close prices against a stale baseline and washing out
    //    the correct book.daily_pnl from that last session.
    //  - "we've received a tick" alone breaks right after today's EOD runs:
    //    liveQuotes isn't cleared when the feed goes quiet, so stale ticks
    //    from before close would still count as "live".
    // Together they cover it: has-ticks rules out weekends/holidays (the
    // feed never sends anything), and the date check rules out the
    // post-EOD-same-day window.
    const todayIST = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const eodDoneToday = book.last_eod_run_date === todayIST;
    const hasLiveTicks = Object.keys(liveQuotes).length > 0;
    if (eodDoneToday || !hasLiveTicks) {
      setPortfolioLive(undefined);
      setPNLLive(undefined);
      return;
    }
    let updatedPortfolio = book.current_cash;
    positions.forEach((p) => {
      const lq       = liveQuotes[p.symbol];
      const curPrice = lq?.ltp ?? p.current_price;
      const curValue = curPrice != null ? curPrice * p.shares : p.current_value;
      updatedPortfolio += curValue;
    })
    setPortfolioLive(updatedPortfolio);
    setPNLLive(updatedPortfolio - book.portfolio_value);
  }, [book, positions, liveQuotes])

  const handleTabChange = (t) => {
    setTab(t);
    if (t !== TAB_RECONCILE) {
      setTabLoading(true);
      fetchTab(t).finally(() => setTabLoading(false));
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = book.status === 'active' ? 'paused' : 'active';
    const res = await api.patch(`/live/books/${id}`, { status: newStatus });
    setBook(res.data);
  };

  const handleToggleDryRun = async () => {
    if (book.is_dry_run) {
      setGoLiveDialog(true);
    } else {
      const res = await api.patch(`/live/books/${id}`, { is_dry_run: true });
      setBook(res.data);
    }
  };

  const handleConfirmGoLive = async () => {
    setGoLiveDialog(false);
    const res = await api.patch(`/live/books/${id}`, { is_dry_run: false, confirm_go_live: true });
    setBook(res.data);
  };

  const handleRunDay = async (force = false) => {
    setRunningDay(true);
    try {
      await api.post(`/live/books/${id}/run-day`, force ? { force: true } : {});
      await loadData();
    } catch (err) {
      console.error('Failed to run day', err);
    } finally {
      setRunningDay(false);
    }
  };

  const handleSyncAuth = async () => {
    setSyncingAuth(true);
    try {
      await api.post(`/live/books/${id}/sync-auth`);
      await fetchBook();
    } catch (err) {
      console.error('Failed to sync auth', err);
    } finally {
      setSyncingAuth(false);
    }
  };

  const handleResumeEntries = async () => {
    if (!(await confirm(
      `Reason it tripped: ${describeHaltReason(book)}. Make sure you understand why this fired before resuming — it will not re-trip until the limit is breached again.`,
      { title: `Resume new entries for "${book.name}"?`, confirmText: 'Resume', isDestructive: false }
    ))) return;
    setResumingEntries(true);
    try {
      const res = await api.post(`/live/books/${id}/resume-entries`);
      setBook(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resume entries');
    } finally {
      setResumingEntries(false);
    }
  };

  const handleFlatten = async () => {
    if (!(await confirm(
      `This sells every open position in "${book.name}" at market right now, regardless of stop-loss, ` +
      `target, or holding-period rules, and halts new entries. Existing GTTs are cancelled as each position ` +
      `is closed. This cannot be undone.`,
      { title: 'Flatten this book now?', confirmText: 'Flatten Now', isDestructive: true }
    ))) return;
    setFlattening(true);
    try {
      const res = await api.post(`/live/books/${id}/flatten`);
      const { exited, partial, failed } = res.data;
      if (failed > 0) {
        toast.error(`Flattened ${exited} position(s), ${partial} partial — ${failed} could not be sold and remain held (protected, retrying next cycle).`);
      } else {
        toast.success(`Flattened ${exited} position(s)${partial ? `, ${partial} partial` : ''}. New entries halted.`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to flatten book');
    } finally {
      setFlattening(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    setReconcile(null);
    try {
      const res = await api.post(`/live/books/${id}/reconcile`);
      setReconcile(res.data);
    } catch (err) {
      setReconcile({ error: err.response?.data?.error || 'Failed to reconcile' });
    } finally {
      setReconciling(false);
    }
  };

  const handleDeletePendingPosition = async (position) => {
    if (!(await confirm('This cannot be undone.', {
      title: `Delete pending position for ${position.symbol.replace('.NS', '')}?`, confirmText: 'Delete',
    }))) return;
    try {
      await api.delete(`/live/books/${id}/positions/${position.id}`);
      await fetchTab(TAB_POSITIONS);
      await fetchBook();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete position');
    }
  };

  const handleCloseGhost = async (symbol) => {
    if (!(await confirm(`This closes the ghost position ${symbol} in the database only.`, {
      title: 'Close ghost position?', confirmText: 'Close',
    }))) return;
    try {
      await api.post(`/live/books/${id}/reconcile/close-ghost`, { symbol });
      handleReconcile();
      fetchBook();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close ghost');
    }
  };

  const openExitDialog = (symbol, dbPrice, dbShares) => {
    setExitDialog({ symbol, dbPrice, dbShares });
    setExitPrice(dbPrice != null ? dbPrice.toFixed(2) : '');
  };

  const handleManualExit = async () => {
    if (!exitDialog) return;
    const exit_price = parseFloat(exitPrice);
    if (!Number.isFinite(exit_price) || exit_price <= 0) {
      toast.error('Enter a valid positive exit price.');
      return;
    }
    setExitSubmitting(true);
    try {
      const { symbol } = exitDialog;
      const res = await api.post(`/live/books/${id}/reconcile/manual-exit`, { symbol, exit_price });
      const { pnl, pnl_pct, shares, cash_after } = res.data;
      toast.success(`Recorded exit: ${symbol.replace('.NS', '')} × ${shares} @ ₹${exit_price.toFixed(2)} — P&L ₹${pnl} (${pnl_pct}%). Available cash: ₹${cash_after}`);
      setExitDialog(null);
      handleReconcile();
      fetchBook();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record exit');
    } finally {
      setExitSubmitting(false);
    }
  };

  const handleImportOrphan = async (symbol) => {
    const clean = symbol.replace('.NS', '');
    if (!(await confirm('This imports the position from the broker into this book and places a protective GTT.', {
      title: `Import ${clean}?`, confirmText: 'Import', isDestructive: false,
    }))) return;
    try {
      const res = await api.post(`/live/books/${id}/reconcile/import-orphan`, { symbol });
      const gttMsg = res.data?.gtt_id ? `GTT ${res.data.gtt_id} placed.` : 'No GTT placed (book has no trade config or no price-based exit configured).';
      toast.success(`Imported ${clean}. ${gttMsg}`);
      handleReconcile();
      fetchBook();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to import orphan');
    }
  };

  const handleSyncEntryPrice = async (symbol) => {
    const clean = symbol.replace('.NS', '');
    if (!(await confirm(`This syncs the entry price for ${clean} from the broker's actual average price.`, {
      title: 'Sync entry price?', confirmText: 'Sync', isDestructive: false,
    }))) return;
    try {
      const res = await api.post(`/live/books/${id}/reconcile/sync-entry-price`, { symbol });
      toast.success(`Updated ${clean}: ₹${res.data.old_price?.toFixed(2)} → ₹${res.data.new_price?.toFixed(2)}`);
      handleReconcile();
      fetchBook();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to sync entry price');
    }
  };

  const openFundsDialog = () => {
    setFundsAmount('');
    setFundsDialog(true);
  };

  const handleAdjustFunds = async (sign) => {
    const amount = parseFloat(fundsAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid positive amount.');
      return;
    }
    const delta = sign * amount;
    setFundsSubmitting(true);
    try {
      const res = await api.post(`/live/books/${id}/adjust-funds`, { delta });
      setBook(res.data);
      setFundsDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to adjust funds');
    } finally {
      setFundsSubmitting(false);
    }
  };

  const openRenameDialog = () => {
    setRenameValue(book.name || '');
    setRenameDialog(true);
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!name) {
      toast.error('Enter a valid name.');
      return;
    }
    setRenameSubmitting(true);
    try {
      const res = await api.patch(`/live/books/${id}`, { name });
      setBook(res.data);
      setRenameDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to rename book');
    } finally {
      setRenameSubmitting(false);
    }
  };

  const openRiskDialog = () => {
    setRiskLimitValue(book.daily_loss_limit_pct != null ? String(book.daily_loss_limit_pct) : '');
    setRiskDialog(true);
  };

  const handleSaveRiskLimit = async () => {
    const trimmed = riskLimitValue.trim();
    let pct = null;
    if (trimmed !== '') {
      pct = parseFloat(trimmed);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        toast.error('Enter a daily loss limit between 0 and 100, or clear the field to disable it.');
        return;
      }
    }
    setRiskSubmitting(true);
    try {
      const res = await api.patch(`/live/books/${id}`, { daily_loss_limit_pct: pct });
      setBook(res.data);
      setRiskDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update risk limit');
    } finally {
      setRiskSubmitting(false);
    }
  };

  const liveCalculateCumulativeReturns = () => {
    if(portfolioLive == null || book.initial_funds == null) {
      return null;
    }
    return (portfolioLive / book.initial_funds - 1) * 100;
  }

  if (loading && !book) return <AppLayout><p className="p-8 text-sm text-muted-foreground flex items-center gap-2"><Activity className="animate-spin w-4 h-4"/> Loading live book…</p></AppLayout>;
  if (!book) return <AppLayout><p className="p-8 text-sm text-muted-foreground">Book not found.</p></AppLayout>;

  const cumPct = (liveCalculateCumulativeReturns() ?? book.cumulative_return_pct) || 0;
  const isPositive = cumPct >= 0;

  const bgTintWin = 'bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/20'
  const bgTintLoss = 'bg-rose-500/5 dark:bg-rose-500/10 border-rose-500/20'

  const daily_pnl = pnlLive ?? book.daily_pnl;

  return (
    <AppLayout backTo="/live" backLabel="← Trading">
      
      {/* 1. Header */}
      <div className="px-2 md:px-6 py-4 border-b border-border bg-surface-base flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-bold text-foreground">{book.name}</h1>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wide ${
              book.status === 'active' ? 'bg-win/15 text-win ring-1 ring-win/30' : 'bg-amber-400/15 text-amber-600 ring-1 ring-amber-400/30'
            }`}>
              {book.status}
            </span>
            {!book.is_auth_valid && (
               <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wide bg-destructive/15 text-destructive ring-1 ring-destructive/30 flex items-center gap-1">
                 <AlertCircle className="w-3 h-3"/> Auth Expired
               </span>
            )}
            {book.entries_halted_at && (
               <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full tracking-wide bg-destructive/15 text-destructive ring-1 ring-destructive/30 flex items-center gap-1">
                 <ShieldAlert className="w-3 h-3"/> Entries Halted
               </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <span>{book.active_screener_name || <span className="text-amber-500">⚠ No active helm</span>}</span>
            {book.broker && <span className="uppercase border px-1 rounded text-[10px]">{book.broker}</span>}
            {book.daily_loss_limit_pct != null && (
              <span className="border px-1 rounded text-[10px]" title="Loss limit — checked intraday and at end of day; halts new entries if breached">
                Loss limit {book.daily_loss_limit_pct}%
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleDryRun}
            className={`flex-1 md:flex-none ${book.is_dry_run ? 'border-amber-400 text-amber-600 hover:bg-amber-400/10' : ''}`}
          >
            <FlaskConical className="w-3.5 h-3.5 mr-2" />
            {book.is_dry_run ? 'Go Live' : 'Switch to Paper'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleToggleStatus} className="flex-1 md:flex-none">
            {book.status === 'active' ? <Pause className="w-3.5 h-3.5 mr-2" /> : <Play className="w-3.5 h-3.5 mr-2" />}
            {book.status === 'active' ? 'Pause' : 'Resume'}
          </Button>
          <Button size="sm" onClick={() => handleRunDay(false)} disabled={runningDay || !book.active_screener_id || !book.is_auth_valid} className="flex-1 md:flex-none w-full sm:w-auto">
            <Play className="w-3.5 h-3.5 mr-2" />
            {runningDay ? 'Running…' : 'Run Today'}
          </Button>

          {/* Secondary Actions Dropdown */}
          {/* modal={false}: avoids the body pointer-events lock conflicting with
              the Dialogs opened from menu items (which would otherwise leave the
              page non-interactive after the dialog closes). */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">

              <DropdownMenuItem onClick={openRenameDialog} className="cursor-pointer">
                <Pencil className="w-3.5 h-3.5 mr-2" />
                <span>Rename</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={handleSyncAuth} disabled={syncingAuth} className="cursor-pointer">
                <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncingAuth ? 'animate-spin' : ''}`} />
                <span>Sync Auth</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => handleRunDay(true)} disabled={runningDay} className="cursor-pointer">
                <Calculator className={`w-3.5 h-3.5 mr-2`} />
                <span>Recalculate</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={openFundsDialog} className="cursor-pointer">
                <Wallet className="w-3.5 h-3.5 mr-2" />
                <span>Update Funds</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={openRiskDialog} className="cursor-pointer">
                <ShieldAlert className="w-3.5 h-3.5 mr-2" />
                <span>Risk Settings</span>
              </DropdownMenuItem>

              {!book.is_dry_run && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleFlatten}
                    disabled={flattening}
                    className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <OctagonX className="w-3.5 h-3.5 mr-2" />
                    <span>{flattening ? 'Flattening…' : 'Flatten Now'}</span>
                  </DropdownMenuItem>
                </>
              )}

            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="p-2 md:p-6 max-w-7xl mx-auto flex flex-col gap-6">

        {/* Kill-switch banner — new entries are blocked on every run until this
            is cleared. Existing positions are unaffected: GTTs, trailing stops,
            exits and reconciliation keep running normally. */}
        {book.entries_halted_at && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-xl">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-destructive">
                  New entries halted — {describeHaltReason(book)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Since {new Date(book.entries_halted_at).toLocaleString()}.{' '}
                  {book.entries_halted_reason === 'manual_flatten'
                    ? 'All positions were closed at market when this was triggered.'
                    : 'Existing positions are still monitored and exited normally — only new BUYs are blocked, on every run, until resumed.'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-destructive text-destructive hover:bg-destructive/10 shrink-0"
              onClick={handleResumeEntries}
              disabled={resumingEntries}
            >
              {resumingEntries ? 'Resuming…' : 'Resume Entries'}
            </Button>
          </div>
        )}

        {/* 2. Hero KPI Dashboard - Updated Grid for Balance */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {/* Hero Metric spans 2 columns */}
          <Card className={`col-span-2 flex flex-col justify-between relative overflow-hidden transition-colors ${isPositive ? bgTintWin : bgTintLoss}`}>
            <CardContent className="p-4 pt-4 flex flex-col justify-between h-full">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                {isPositive ? <TrendingUp className="w-16 h-16 text-win" /> : <TrendingDown className="w-16 h-16 text-loss" />}
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground/80 mb-2">Live Portfolio Value</span>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-foreground tracking-tight tabular-nums">
                  {fmtMoney(portfolioLive != null ? portfolioLive :book.portfolio_value)}
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium mt-1 tabular-nums ${isPositive ? 'text-win' : 'text-loss'}`}>
                  {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {fmtPct(cumPct)} all time
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Secondary Metrics */}
          {/* daily_pnl / total_pnl are `null` until there's a real number to show (see
              fmtMoney's own '—' fallback) — treat null as "unknown", not "non-negative",
              so the card doesn't render green/win-tinted for a value that's actually absent. */}
          {[
            { label: "Today's P&L", val: fmtMoney(daily_pnl), color: daily_pnl == null ? '' : (daily_pnl >= 0 ? 'text-win' : 'text-loss'),
              style: daily_pnl == null ? '' : (daily_pnl >= 0 ? bgTintWin : bgTintLoss)
            },
            { label: 'Realised P&L', val: fmtMoney(book.summary?.total_pnl), color: book.summary?.total_pnl == null ? '' : (book.summary.total_pnl >= 0 ? 'text-win' : 'text-loss'),
              style: book.summary?.total_pnl == null ? '' : (book.summary.total_pnl >= 0 ? bgTintWin : bgTintLoss)
            },
            { label: 'Cash Available', val: fmtMoney(book.current_cash),
              style: ''
            },
            { label: 'Win Rate', val: `${book.summary?.win_rate_pct?.toFixed(1) || '0'}%`,
              style: ''
            },
          ].map(stat => (
            <Card key={stat.label} className={`flex flex-col justify-center ${stat.style}`}>
              <CardContent className="p-4 pt-4 flex flex-col justify-center">
                <span className="text-xs uppercase font-semibold tracking-wider text-muted-foreground mb-1">{stat.label}</span>
                <span className={`text-lg md:text-xl tabular-nums ${stat.color || 'text-foreground'}`}>{stat.val}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        {equity && equity.length > 0 && (
          <Card>
            <CardContent className="p-4 pt-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Live Equity Curve</h3>
              <div className="h-64">
                <EquityChart
                  data={equity.map(e => ({ date: e.date, value: e.value }))}
                  initialFunds={book.initial_funds}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. Tabs Navigation */}
        <div className="relative">
          <div
            ref={tabScrollRef}
            onScroll={updateTabOverflow}
            className="flex gap-2 border-b border-border overflow-x-auto no-scrollbar"
          >
            {[TAB_POSITIONS, TAB_ORDERS, TAB_TRADES, TAB_EQUITY, TAB_DIVERGENCE, TAB_RECONCILE].map(t => (
              <button
                key={t}
                onClick={() => handleTabChange(t)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                  tab === t
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {TAB_LABELS[t] || (t.charAt(0).toUpperCase() + t.slice(1))}
                {tab === t && tabLoading && (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                )}
                {t === TAB_POSITIONS && streamStatus === 'live' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse" />
                )}
              </button>
            ))}
          </div>

          {/* Edge fades — the scrollbar is hidden (no-scrollbar), so these are
              the only cue that Strategy Sync / Reconcile sit off-screen on
              narrow viewports. Each only shows while there's actually more
              to scroll to in that direction. */}
          {tabOverflow.left && (
            <div className="pointer-events-none absolute left-0 top-0 bottom-px w-8 bg-linear-to-r from-background to-transparent" />
          )}
          {tabOverflow.right && (
            <div className="pointer-events-none absolute right-0 top-0 bottom-px w-8 bg-linear-to-l from-background to-transparent" />
          )}
        </div>

        {/* 4. Tab Content */}
        <div>
          
          {/* POSITIONS TAB */}
          {tab === TAB_POSITIONS && (
            <div className="flex flex-col gap-3">
              <DataTable
                empty="No open live positions."
                head={POSITIONS_HEAD}
                rows={positions.map(p => {
                  const lq       = liveQuotes[p.symbol];
                  const curPrice = lq?.ltp ?? p.current_price;
                  const curValue = curPrice != null ? curPrice * p.shares : p.current_value;
                  const unrlPnl  = (curPrice != null && p.entry_price) ? (curPrice - p.entry_price) * p.shares : p.unrealised_pnl;
                  const unrlPct  = (curPrice != null && p.entry_price) ? (curPrice / p.entry_price - 1) * 100 : p.unrealised_pnl_pct;
                  const flashed  = flashedSymbols.has(p.symbol);
                  const flashCls = flashed && lq?.direction === 'up' ? 'bg-win/10' : flashed && lq?.direction === 'down' ? 'bg-loss/10' : '';
                  const gtt_targets = p.gtt_data?.condition?.trigger_values
                  const isExitQueued = p.exit_queued ?? false;
                  return (
                  <Fragment key={p.id}>
                    <tr className={`hover:bg-surface-hover transition-colors duration-700 ${flashCls}`}>
                      <td className="px-4 py-3 font-bold text-foreground">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setChartPosition(p)}
                            className="group inline-flex items-center gap-1.5 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            title="Plot the screener's indicators around this entry"
                            aria-label={`Indicators for ${p.symbol.replace('.NS', '')}`}
                          >
                            <LineChart size={13} />
                            <span className='pe-3'>{p.symbol.replace('.NS', '')}</span>
                          </button>
                          {p.is_pending && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0 "
                              title="Queued to enter at next market open — not yet placed with the broker"
                            >
                              <ClockArrowDown className='w-3 h-3' /> Pending
                            </span>
                          )}
                          {isExitQueued && (
                            <ClockArrowUp className='w-3.5 h-3.5 text-primary' />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{p.shares}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDate(p.entry_date)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(p.entry_price)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground tabular-nums">{curPrice ? `₹${curPrice.toFixed(2)}` : '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground tabular-nums">{curValue ? fmtMoney(curValue) : '—'}</td>
                      <td className={`px-4 py-3 text-xs font-medium tabular-nums ${unrlPnl == null ? 'text-muted-foreground' : (unrlPnl >= 0 ? 'text-win' : 'text-loss')}`}>
                        {unrlPnl != null ? `${unrlPnl >= 0 ? '+' : ''}${fmtMoney(unrlPnl)} (${fmtPct(unrlPct)})` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{p.holding_days}</td>
                      <td className="px-4 py-2">
                        {p.is_pending && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeletePendingPosition(p)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                    {gtt_targets && gtt_targets.length === 2 && (
                      <tr className='border-b border-border/50'>
                        <td className={'text-xs font-medium'} colSpan={POSITIONS_HEAD.length}>
                          <div className='p-4 pt-0'>
                            <TradeVisualizer
                              stopLoss={gtt_targets[0]}
                              currentPrice={curPrice}
                              profitTarget={gtt_targets[1]}
                              entryPrice={p.entry_price}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
              />

              {/* Mobile Positions Cards */}
              <div className="flex flex-col gap-3 md:hidden">
                {positions.map(p => {
                  const lq       = liveQuotes[p.symbol];
                  const curPrice = lq?.ltp ?? p.current_price;
                  const curValue = curPrice != null ? curPrice * p.shares : p.current_value;
                  const unrlPnl  = (curPrice != null && p.entry_price) ? (curPrice - p.entry_price) * p.shares : p.unrealised_pnl;
                  const unrlPct  = (curPrice != null && p.entry_price) ? (curPrice / p.entry_price - 1) * 100 : p.unrealised_pnl_pct;
                  const flashed  = flashedSymbols.has(p.symbol);
                  const flashCls = flashed && lq?.direction === 'up' ? 'ring-1 ring-win/30' : flashed && lq?.direction === 'down' ? 'ring-1 ring-loss/30' : '';
                  const gtt_targets = p.gtt_data?.condition?.trigger_values
                  const isExitQueued = p.exit_queued ?? false;
                  return (
                  <Card key={p.id} className={`transition-colors duration-700 ${flashCls}`}>
                    <CardContent className="p-4 flex flex-col gap-3">
                      <div className="flex justify-between items-center pb-2 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setChartPosition(p)}
                            className="inline-flex items-center gap-1.5 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label={`Indicators for ${p.symbol.replace('.NS', '')}`}
                          >
                            <LineChart size={14} />
                            <span className="font-bold text-lg text-foreground">{p.symbol.replace('.NS', '')}</span>
                          </button>
                          {p.is_pending && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0"
                              title="Queued to enter at next market open — not yet placed with the broker"
                            >
                              <ClockArrowDown className='w-3 h-3' /> Pending
                            </span>
                          )}
                          {isExitQueued && (
                            <ClockArrowUp className='w-3.5 h-3.5 text-primary' />
                          )}
                        </div>
                        {p.is_pending ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeletePendingPosition(p)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                          </Button>
                        ) : (
                          <span className={`text-xs font-bold px-2 py-1 rounded tabular-nums ${unrlPnl == null ? 'bg-muted text-muted-foreground' : (unrlPnl >= 0 ? 'bg-win/15 text-win' : 'bg-loss/15 text-loss')}`}>
                            {unrlPnl != null && unrlPnl >= 0 ? '+' : ''}{fmtMoney(unrlPnl)} ({fmtPct(unrlPct)})
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-y-2 text-xs">
                        <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Value</span><span className="font-mono tabular-nums">{curValue ? fmtMoney(curValue) : '—'}</span></div>
                        <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Current ₹</span><span className="font-mono tabular-nums">{curPrice ? `₹${curPrice.toFixed(2)}` : '—'}</span></div>
                        <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Entry ₹</span><span className="font-mono">{fmtPrice(p.entry_price)}</span></div>
                        <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Shares</span><span className="font-mono">{p.shares}</span></div>
                      </div>
                      {gtt_targets && gtt_targets.length === 2 && (
                        <div className="pt-3 border-t border-border/50">
                          <TradeVisualizer
                            stopLoss={gtt_targets[0]}
                            currentPrice={curPrice}
                            profitTarget={gtt_targets[1]}
                            entryPrice={p.entry_price}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* ORDERS TAB */}
          {tab === TAB_ORDERS && (
            <div className="flex flex-col gap-3">
              <DataTable
                empty="No orders placed today."
                head={['Symbol', 'Date', 'Side', 'Status', 'Quantity', 'Price', 'Message']}
                rows={orders.map(o => (
                  <tr key={o.id} className="border-b border-border/50 hover:bg-surface-hover transition-colors">
                    <td className="px-4 py-3 font-bold text-foreground">{o.symbol.replace('.NS', '')}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{o.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${o.side === 'BUY' ? 'bg-win/15 text-win' : 'bg-loss/15 text-loss'}`}>
                        {o.side}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                        o.status === 'filled' ? 'bg-win/15 text-win' : 
                        o.status === 'rejected' ? 'bg-destructive/15 text-destructive' : 
                        'bg-amber-400/15 text-amber-600'
                      }`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{o.quantity}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{o.filled_price ? `₹${o.filled_price.toFixed(2)}` : '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px] truncate" title={o.broker_message}>{o.broker_message || '—'}</td>
                  </tr>
                ))}
              />

              {/* Mobile Orders Cards */}
              <div className="flex flex-col gap-3 md:hidden">
                {orders.map(o => (
                  <div key={o.id} className="p-4 bg-card shadow-sm bg-surface-base border border-border rounded-xl flex flex-col gap-3">
                    <div className="flex justify-between items-center pb-2 border-b border-border/50">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg text-foreground">{o.symbol.replace('.NS', '')}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${o.side === 'BUY' ? 'bg-win/15 text-win' : 'bg-loss/15 text-loss'}`}>{o.side}</span>
                      </div>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                        o.status === 'filled' ? 'bg-win/15 text-win' :
                        o.status === 'rejected' ? 'bg-destructive/15 text-destructive' :
                        'bg-amber-400/15 text-amber-600'
                      }`}>
                        {o.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-xs">
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Date</span><span className="font-mono">{o.created_at?.slice(0, 16).replace('T', ' ')}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Price</span><span className="font-mono">{o.filled_price ? `₹${o.filled_price.toFixed(2)}` : '—'}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Quantity</span><span className="font-mono">{o.quantity}</span></div>
                    </div>
                    {o.broker_message && (
                      <div className="mt-1 p-2 bg-destructive/10 text-destructive text-xs rounded border border-destructive/20">
                        {o.broker_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TRADES TAB */}
          {tab === TAB_TRADES && (
            <div className="flex flex-col gap-3">
              <DataTable
                empty="No closed live trades yet."
                head={['Symbol', 'Shares', 'Entry', 'Exit', 'Entry ₹', 'Exit ₹', 'P&L', 'P&L %', 'Days', 'Reason']}
                rows={trades.map(t => (
                  <tr key={t.id} className="border-b border-border/50 hover:bg-surface-hover transition-colors">
                    <td className="px-4 py-3 font-bold text-foreground">{t.symbol.replace('.NS', '')}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.shares}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDate(t.entry_date)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDate(t.exit_date)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(t.entry_price)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(t.exit_price)}</td>
                    <td className={`px-4 py-3 text-xs font-medium ${t.pnl >= 0 ? 'text-win' : 'text-loss'}`}>
                      {t.pnl >= 0 ? '+' : ''}{fmtMoney(t.pnl)}
                    </td>
                    <td className={`px-4 py-3 text-xs font-medium ${t.pnl_pct >= 0 ? 'text-win' : 'text-loss'}`}>
                      {fmtPct(t.pnl_pct)}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{t.holding_days}</td>
                    <td className="px-4 py-3 text-[10px] text-muted-foreground uppercase">{t.exit_reason}</td>
                  </tr>
                ))}
              />

              {/* Mobile Trades Cards */}
              <div className="flex flex-col gap-3 md:hidden">
                {trades.map(t => (
                  <div key={t.id} className="p-4 bg-card shadow-sm bg-surface-base border border-border rounded-xl flex flex-col gap-3">
                    <div className="flex justify-between items-center pb-2 border-b border-border/50">
                      <span className="font-bold text-lg text-foreground">{t.symbol.replace('.NS', '')}</span>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${t.pnl >= 0 ? 'bg-win/15 text-win' : 'bg-loss/15 text-loss'}`}>
                         {t.pnl >= 0 ? '+' : ''}{fmtMoney(t.pnl)} ({fmtPct(t.pnl_pct)})
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-xs">
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Entry</span><span className="font-mono">{fmtDate(t.entry_date)}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Exit</span><span className="font-mono">{fmtDate(t.exit_date)}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Entry ₹</span><span className="font-mono">{fmtPrice(t.entry_price)}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Exit ₹</span><span className="font-mono">{fmtPrice(t.exit_price)}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Reason</span><span className="text-[10px] uppercase">{t.exit_reason}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Days</span><span className="font-mono">{t.holding_days}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* EQUITY TAB */}
          {tab === TAB_EQUITY && (
            <div className="flex flex-col gap-6">
              {equity.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">No ledger entries yet — the daily EOD job writes one snapshot per trading day.</div>
              ) : (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Daily Ledger (Last 60 Days)</h3>
                    <DataTable
                      head={['Date', 'Portfolio Value', 'Cash', 'Positions Value', 'Daily P&L', 'Cum. Return']}
                      rows={[...equity].reverse().slice(0, 60).map(e => (
                        <tr key={e.date} className="border-b border-border/50 hover:bg-surface-hover transition-colors">
                          <td className="px-4 py-2 text-xs font-mono text-muted-foreground">{e.date}</td>
                          <td className="px-4 py-2 font-medium text-foreground">{fmtMoney(e.value)}</td>
                          <td className="px-4 py-2 text-muted-foreground">{fmtMoney(e.cash)}</td>
                          <td className="px-4 py-2 text-muted-foreground">{fmtMoney(e.positions_value)}</td>
                          <td className={`px-4 py-2 text-xs font-medium ${e.daily_pnl == null ? 'text-muted-foreground' : (e.daily_pnl >= 0 ? 'text-win' : 'text-loss')}`}>
                            {e.daily_pnl != null ? `${e.daily_pnl >= 0 ? '+' : ''}${fmtMoney(e.daily_pnl)}` : '—'}
                          </td>
                          <td className={`px-4 py-2 text-xs font-medium ${e.cumulative_return_pct == null ? 'text-muted-foreground' : (e.cumulative_return_pct >= 0 ? 'text-win' : 'text-loss')}`}>
                            {fmtPct(e.cumulative_return_pct)}
                          </td>
                        </tr>
                      ))}
                    />

                    {/* Mobile Trades Cards */}
                    <div className="flex flex-col gap-3 md:hidden">
                      {[...equity].reverse().slice(0, 60).map(e => (
                        <div key={e.date} className="p-4 bg-card shadow-sm bg-surface-base border border-border rounded-xl flex flex-col gap-3">
                          <div className="flex justify-between items-center pb-2 border-b border-border/50">
                            <span className="font-bold text-lg text-foreground">{e.date}</span>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${e.cumulative_return_pct == null ? 'bg-muted text-muted-foreground' : (e.cumulative_return_pct >= 0 ? 'bg-win/15 text-win' : 'bg-loss/15 text-loss')}`}>
                              {fmtPct(e.cumulative_return_pct)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-y-2 text-xs">
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Portfolio Value</span><span className="font-mono">{fmtMoney(e.value)}</span></div>
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Cash</span><span className="font-mono">{fmtMoney(e.cash)}</span></div>
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Positions Value</span><span className="font-mono">{fmtMoney(e.positions_value)}</span></div>
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Daily P&L</span>
                            <span className={`font-mono ${e.daily_pnl == null ? 'text-muted-foreground' : (e.daily_pnl >= 0 ? 'text-win' : 'text-loss')}`}>{e.daily_pnl != null ? `${e.daily_pnl >= 0 ? '+' : ''}${fmtMoney(e.daily_pnl)}` : '—'}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STRATEGY SYNC TAB */}
          {tab === TAB_DIVERGENCE && (
            <div className="flex flex-col gap-4">
              {!book.active_screener_id || !book.active_trade_config_id ? (
                <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                  This book isn't linked to a strategy pair yet — Strategy Sync starts tracking once a helm run activates a screener/trade-config pair.
                </div>
              ) : divergence.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                  No divergence data yet — the daily EOD job computes one snapshot per trading day; check back after tomorrow's run.
                </div>
              ) : (() => {
                const latest = divergence[divergence.length - 1];
                const scoreCls = {
                  low:    'bg-win/15 text-win',
                  medium: 'bg-amber-400/15 text-amber-600',
                  high:   'bg-loss/15 text-loss',
                  insufficient_data: 'bg-muted text-muted-foreground',
                }[latest.divergence_score] || 'bg-muted text-muted-foreground';
                const bucketCls = { low: 'text-win', medium: 'text-amber-600', high: 'text-loss' };

                const metricRows = [
                  { label: 'Cumulative Return', live: latest.live_cumulative_return_pct, bt: latest.backtest_cumulative_return_pct, div: latest.return_divergence_pp, bucket: latest.return_divergence_bucket },
                  { label: 'Win Rate',          live: latest.live_win_rate_pct,          bt: latest.backtest_win_rate_pct,          div: latest.win_rate_divergence_pp,      bucket: latest.win_rate_divergence_bucket },
                  { label: 'Max Drawdown',      live: latest.live_max_drawdown_pct,      bt: latest.backtest_max_drawdown_pct,      div: latest.drawdown_divergence_pp,      bucket: latest.drawdown_divergence_bucket },
                  { label: 'Avg Trade P&L',     live: latest.live_avg_trade_pnl_pct,     bt: latest.backtest_avg_trade_pnl_pct,     div: latest.avg_trade_pnl_divergence_pp, bucket: latest.avg_trade_pnl_divergence_bucket },
                ].filter(m => m.live != null);

                const staleDays = latest.baseline_tested_through
                  ? Math.round((new Date(latest.date) - new Date(latest.baseline_tested_through)) / 86400000)
                  : null;

                return (
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between bg-card border border-border rounded-lg p-4">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">Live vs Backtest Divergence</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          As of {fmtDate(latest.date)}
                          {latest.backtest_window_exceeded ? ' — live has run past the backtest testing window; return/drawdown compared at the last available backtest point.' : ''}
                        </p>
                        {latest.baseline_tested_through && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Backtest baseline tested through {fmtDate(latest.baseline_tested_through)}
                            {staleDays != null && staleDays > 0 ? ` (${staleDays} day${staleDays === 1 ? '' : 's'} before this snapshot)` : ''}
                            {staleDays != null && staleDays > 90 && (
                              <span className="text-amber-600 font-medium"> — consider re-testing this pair; market conditions may have shifted.</span>
                            )}
                          </p>
                        )}
                      </div>
                      <span className={`text-xs font-bold uppercase px-3 py-1.5 rounded whitespace-nowrap ${scoreCls}`}>
                        {latest.divergence_score === 'insufficient_data' ? 'Insufficient Data' : latest.divergence_score}
                      </span>
                    </div>

                    <DataTable
                      empty="No comparable metrics yet."
                      head={['Metric', 'Live', 'Backtest', 'Δ (pp)', 'Bucket']}
                      rows={metricRows.map(m => (
                        <tr key={m.label} className="border-b border-border/50 hover:bg-surface-hover transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground">{m.label}</td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-foreground">{fmtPct(m.live)}</td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">{fmtPct(m.bt)}</td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">{m.div != null ? m.div.toFixed(2) : '—'}</td>
                          <td className={`px-4 py-3 font-semibold uppercase text-[10px] ${bucketCls[m.bucket] || 'text-muted-foreground'}`}>{m.bucket || '—'}</td>
                        </tr>
                      ))}
                    />

                    {/* Mobile metric cards */}
                    <div className="flex flex-col gap-3 md:hidden">
                      {metricRows.map(m => (
                        <div key={m.label} className="p-4 bg-card shadow-sm bg-surface-base border border-border rounded-xl flex flex-col gap-3">
                          <div className="flex justify-between items-center pb-2 border-b border-border/50">
                            <span className="font-bold text-base text-foreground">{m.label}</span>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${bucketCls[m.bucket] || 'text-muted-foreground'}`}>{m.bucket || '—'}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-y-2 text-xs">
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Live</span><span className="font-mono">{fmtPct(m.live)}</span></div>
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Backtest</span><span className="font-mono">{fmtPct(m.bt)}</span></div>
                            <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Δ (pp)</span><span className="font-mono">{m.div != null ? m.div.toFixed(2) : '—'}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {latest.backtest_equity_curve && latest.backtest_equity_curve.length > 0 && (() => {
                      // Scope the live series to the same date range the backtest
                      // actually covers, and rebase both to 0% at that shared start —
                      // otherwise a backtest window that's much shorter than the
                      // book's full live history renders as an invisible sliver, and
                      // cumulative_return_pct (inception-relative) wouldn't line up
                      // with the backtest's own 0%-at-its-start baseline anyway.
                      const firstBtDate = latest.backtest_equity_curve[0].date;
                      const scoped = equity.filter(e => e.date >= firstBtDate);
                      const startValue = scoped[0]?.value;
                      const liveData = startValue
                        ? scoped.map(e => ({ date: e.date, value: (e.value - startValue) / startValue * 100 }))
                        : [];
                      if (!liveData.length) return null;
                      return (
                      <Card>
                        <CardContent className="p-4 pt-4">
                          <h3 className="text-sm font-semibold text-foreground mb-4">Live vs Backtest Return (%)</h3>
                          <p className="text-xs text-muted-foreground mb-4 -mt-2">
                            Both series rebased to 0% at {fmtDate(firstBtDate)} (when the backtest window starts) — shown as % return, not ₹ value, since deposits/withdrawals to this book aren't something the backtest has any concept of.
                          </p>
                          <EquityChart
                            data={liveData}
                            initialFunds={0}
                            benchmarkData={latest.backtest_equity_curve}
                            benchmarkLabel="Backtest"
                          />
                        </CardContent>
                      </Card>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          )}

          {/* RECONCILE TAB */}
          {tab === TAB_RECONCILE && (
            <div className="flex flex-col gap-4 max-w-4xl">
              <div className="flex items-center justify-between bg-surface-card border border-border rounded-lg p-4">
                 <div>
                   <h3 className="text-sm font-semibold text-foreground">Broker Reconciliation</h3>
                   <p className="text-xs text-muted-foreground mt-1">Compare local database positions against actual broker holdings.</p>
                 </div>
                 <Button onClick={handleReconcile} disabled={reconciling}>
                   <RefreshCw className={`w-4 h-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} />
                   {reconciling ? 'Checking…' : 'Run Check'}
                 </Button>
              </div>

              {reconcile && (
                <div className="flex flex-col gap-6">
                  {reconcile.error ? (
                    <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive flex items-center gap-2">
                       <AlertCircle className="w-4 h-4"/> {reconcile.error}
                    </div>
                  ) : (
                    <>
                      {/* Matched — may have wrong entry price */}
                      <div className="flex flex-col gap-2">
                        <h4 className="text-sm font-semibold text-green-600 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4"/> Matched
                        </h4>
                        <DataTable
                           empty="No matched positions."
                           head={['Symbol', 'Shares', 'DB Price', 'Broker Price', 'Action']}
                           rows={(reconcile.matched || []).map(r => (
                             <tr key={r.symbol} className="border-b border-border/50 hover:bg-surface-hover">
                               <td className="px-4 py-3 font-medium text-foreground">{r.symbol.replace('.NS', '')}</td>
                               <td className="px-4 py-3 text-muted-foreground">{r.shares}</td>
                               <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.db_price)}</td>
                               <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.broker_price)}</td>
                               <td className="px-4 py-2">
                                 {Math.abs((r.db_price || 0) - (r.broker_price || 0)) > 0.01 && (
                                   <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleSyncEntryPrice(r.symbol)}>
                                     Sync Price
                                   </Button>
                                 )}
                               </td>
                             </tr>
                           ))}
                        />
                        <div className="flex flex-col gap-3 md:hidden">
                          {(reconcile.matched || []).map(r => (
                            <ReconcileMobileCard
                              key={r.symbol}
                              symbol={r.symbol}
                              fields={[['Shares', r.shares], ['DB Price', fmtPrice(r.db_price)], ['Broker Price', fmtPrice(r.broker_price)]]}
                              action={Math.abs((r.db_price || 0) - (r.broker_price || 0)) > 0.01 && (
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleSyncEntryPrice(r.symbol)}>
                                  Sync Price
                                </Button>
                              )}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Mismatched quantity */}
                      {(reconcile.mismatched || []).length > 0 && (
                        <div className="flex flex-col gap-2">
                          <h4 className="text-sm font-semibold text-amber-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4"/> Quantity Mismatch
                          </h4>
                          <DataTable
                             empty="No mismatched positions."
                             head={['Symbol', 'DB Shares', 'Broker Shares', 'DB Price', 'Broker Price', 'Action']}
                             rows={(reconcile.mismatched || []).map(r => (
                               <tr key={r.symbol} className="border-b border-border/50 hover:bg-surface-hover">
                                 <td className="px-4 py-3 font-medium text-foreground">{r.symbol.replace('.NS', '')}</td>
                                 <td className="px-4 py-3 text-muted-foreground">{r.db_shares}</td>
                                 <td className="px-4 py-3 text-muted-foreground">{r.broker_shares}</td>
                                 <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.db_price)}</td>
                                 <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.broker_price)}</td>
                                 <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openExitDialog(r.symbol, r.broker_price, r.db_shares)}>
                                     Record Exit
                                   </Button>
                                 </td>
                               </tr>
                             ))}
                          />
                          <div className="flex flex-col gap-3 md:hidden">
                            {(reconcile.mismatched || []).map(r => (
                              <ReconcileMobileCard
                                key={r.symbol}
                                symbol={r.symbol}
                                fields={[['DB Shares', r.db_shares], ['Broker Shares', r.broker_shares], ['DB Price', fmtPrice(r.db_price)], ['Broker Price', fmtPrice(r.broker_price)]]}
                                action={
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openExitDialog(r.symbol, r.broker_price, r.db_shares)}>
                                    Record Exit
                                  </Button>
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Orphans — at broker but not in DB */}
                      {(reconcile.orphans || []).length > 0 && (
                        <div className="flex flex-col gap-2">
                          <h4 className="text-sm font-semibold text-amber-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4"/> Orphans (at Broker, not in DB)
                          </h4>
                          <DataTable
                             empty="No orphan positions."
                             head={['Symbol', 'Broker Shares', 'Broker Price', 'Last Price', 'Action']}
                             rows={(reconcile.orphans || []).filter(r => r.broker_shares > 0).map(r => (
                               <tr key={r.symbol} className="border-b border-border/50 hover:bg-surface-hover">
                                 <td className="px-4 py-3 font-medium text-foreground">{r.symbol.replace('.NS', '')}</td>
                                 <td className="px-4 py-3 text-muted-foreground">{r.broker_shares}</td>
                                 <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.broker_price)}</td>
                                 <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.last_price)}</td>
                                 <td className="px-4 py-2">
                                   <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleImportOrphan(r.symbol)}>
                                     Import + place GTT
                                   </Button>
                                 </td>
                               </tr>
                             ))}
                          />
                          <div className="flex flex-col gap-3 md:hidden">
                            {(reconcile.orphans || []).filter(r => r.broker_shares > 0).map(r => (
                              <ReconcileMobileCard
                                key={r.symbol}
                                symbol={r.symbol}
                                fields={[['Broker Shares', r.broker_shares], ['Broker Price', fmtPrice(r.broker_price)], ['Last Price', fmtPrice(r.last_price)]]}
                                action={
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleImportOrphan(r.symbol)}>
                                    Import + place GTT
                                  </Button>
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Ghosts — in DB but not at broker */}
                      <div className="flex flex-col gap-2">
                        <h4 className="text-sm font-semibold text-destructive flex items-center gap-2">
                          <AlertCircle className="w-4 h-4"/> Ghosts (in DB, not at Broker)
                        </h4>
                        <DataTable
                           empty="No ghost positions."
                           head={['Symbol', 'DB Shares', 'DB Price', 'Entry Date', 'Action']}
                           rows={(reconcile.ghosts || []).map(r => (
                             <tr key={r.symbol} className="border-b border-border/50 hover:bg-surface-hover">
                               <td className="px-4 py-3 font-medium text-foreground">{r.symbol.replace('.NS', '')}</td>
                               <td className="px-4 py-3 text-muted-foreground">{r.db_shares}</td>
                               <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtPrice(r.db_price)}</td>
                               <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDate(r.entry_date)}</td>
                               <td className="px-4 py-2">
                                 <div className="flex gap-2">
                                   <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openExitDialog(r.symbol, r.db_price, r.db_shares)}>
                                     Record Exit
                                   </Button>
                                   <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive/10 h-7 text-xs" onClick={() => handleCloseGhost(r.symbol)}>
                                     Remove from DB
                                   </Button>
                                 </div>
                               </td>
                             </tr>
                           ))}
                        />
                        <div className="flex flex-col gap-3 md:hidden">
                          {(reconcile.ghosts || []).map(r => (
                            <ReconcileMobileCard
                              key={r.symbol}
                              symbol={r.symbol}
                              fields={[['DB Shares', r.db_shares], ['DB Price', fmtPrice(r.db_price)], ['Entry Date', fmtDate(r.entry_date)]]}
                              action={
                                <>
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openExitDialog(r.symbol, r.db_price, r.db_shares)}>
                                    Record Exit
                                  </Button>
                                  <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive/10 h-7 text-xs" onClick={() => handleCloseGhost(r.symbol)}>
                                    Remove from DB
                                  </Button>
                                </>
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      <Dialog open={goLiveDialog} onOpenChange={setGoLiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" /> Go Live?
            </DialogTitle>
            <DialogDescription>
              This will disable paper mode. <strong className="text-foreground">Real orders will be placed with Zerodha.</strong> Make sure your Kite credentials and token are valid before continuing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setGoLiveDialog(false)}>Cancel</Button>
            <Button className="bg-destructive hover:bg-destructive/90 text-destructive-foreground" onClick={handleConfirmGoLive}>
              Go Live
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!exitDialog} onOpenChange={(open) => { if (!open) setExitDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockArrowDown className="w-4 h-4" /> Record Exit{exitDialog ? ` — ${exitDialog.symbol.replace('.NS', '')}` : ''}
            </DialogTitle>
            <DialogDescription>
              Record an exit that happened outside the system. This writes a closed trade with realized P&amp;L{exitDialog?.dbShares != null ? ` for ${exitDialog.dbShares} shares` : ''}, cancels any resting protective GTT, and removes the open position.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <label className="text-sm text-muted-foreground">Exit price (₹) — the price you actually sold at</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={exitPrice}
              onChange={(e) => setExitPrice(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !exitSubmitting) handleManualExit(); }}
              autoFocus
              placeholder="e.g. 123.45"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setExitDialog(null)} disabled={exitSubmitting}>Cancel</Button>
            <Button onClick={handleManualExit} disabled={exitSubmitting}>
              {exitSubmitting ? 'Recording…' : 'Record Exit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={fundsDialog} onOpenChange={(open) => { if (!open) setFundsDialog(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Manage Funds
            </DialogTitle>
            <DialogDescription>
              Deposit or withdraw cash. This adjusts both available cash and the
              return baseline, so deposits/withdrawals don't distort performance.
              Current cash: {fmtMoney(book.current_cash)}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <label className="text-sm text-muted-foreground">Amount (₹)</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={fundsAmount}
              onChange={(e) => setFundsAmount(e.target.value)}
              autoFocus
              placeholder="e.g. 50000"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setFundsDialog(false)} disabled={fundsSubmitting}>Cancel</Button>
            <Button
              variant="outline"
              className="border-loss text-loss hover:bg-loss/10"
              onClick={() => handleAdjustFunds(-1)}
              disabled={fundsSubmitting}
            >
              {fundsSubmitting ? 'Working…' : 'Withdraw'}
            </Button>
            <Button onClick={() => handleAdjustFunds(1)} disabled={fundsSubmitting}>
              {fundsSubmitting ? 'Working…' : 'Add Funds'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDialog} onOpenChange={(open) => { if (!open) setRenameDialog(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Rename Book
            </DialogTitle>
            <DialogDescription>
              Give this live book a new name.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <label className="text-sm text-muted-foreground">Name</label>
            <Input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !renameSubmitting) handleRename(); }}
              autoFocus
              placeholder="e.g. Momentum Live"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRenameDialog(false)} disabled={renameSubmitting}>Cancel</Button>
            <Button onClick={handleRename} disabled={renameSubmitting}>
              {renameSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={riskDialog} onOpenChange={(open) => { if (!open) setRiskDialog(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Risk Settings
            </DialogTitle>
            <DialogDescription>
              If the book's value drops this much from the prior day's snapshot, new entries are
              halted on every subsequent run — existing positions keep being monitored and exited
              normally — until manually resumed. Clear the field to disable this kill switch.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <label className="text-sm text-muted-foreground">Daily loss limit (%)</label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={riskLimitValue}
              onChange={(e) => setRiskLimitValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !riskSubmitting) handleSaveRiskLimit(); }}
              autoFocus
              placeholder="e.g. 5 (leave blank for no limit)"
            />
            {book.entries_halted_at && (
              <p className="text-xs text-destructive mt-1">
                Entries are currently halted ({describeHaltReason(book)}). Use "Resume Entries" on
                the banner above to clear that — saving here only changes the limit itself.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRiskDialog(false)} disabled={riskSubmitting}>Cancel</Button>
            <Button onClick={handleSaveRiskLimit} disabled={riskSubmitting}>
              {riskSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Indicator chart for one open position.
          The book's active screener is the only provenance available — a
          position does not record the screener that opened it — so the dialog
          flags the case where that screener does not match on the entry bar. */}
      <SignalChartDialog
        open={chartPosition != null}
        onOpenChange={(isOpen) => !isOpen && setChartPosition(null)}
        screenerId={book?.active_screener_id}
        screenerName={book?.active_screener_name}
        symbol={chartPosition?.symbol}
        signalDate={chartPosition?.entry_signal_date || chartPosition?.entry_date}
        subtitle={
          chartPosition
            ? `${chartPosition.entry_signal_date ? 'Signal' : 'Entry'} ${chartPosition.entry_signal_date || chartPosition.entry_date}`
            : undefined
        }
        deps={chartDeps}
        onToggleDeps={setChartDeps}
      />

      {confirmDialog}
    </AppLayout>
  );
}